import type { MealPlanEntry, MealPlanEntryWriteInput, MealPlanWeek } from '@recipes/contracts';
import { Alert, Button, Spinner } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, eachDayOfInterval, format, parseISO, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, ShoppingCart, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { EntryDialog, type EntryDialogState } from '@/features/meal-plan/entry-dialog';
import { ShoppingFromPlanDialog } from '@/features/meal-plan/shopping-dialog';
import { SuggestionsDialog } from '@/features/meal-plan/suggestions-dialog';
import { WeekGrid, dateLocale, type SlotTarget } from '@/features/meal-plan/week-grid';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { HouseholdSelect } from '@/features/shared/household-select';
import { useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, isVersionConflict, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

const ISO = 'yyyy-MM-dd';

export function MealPlanPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const aiEnabled = useFeature('ai');
  const [params, setParams] = useSearchParams();
  const weekParam = params.get('week');
  const weekStart = useMemo(
    () => startOfWeek(weekParam ? parseISO(weekParam) : new Date(), { weekStartsOn: 1 }),
    [weekParam],
  );
  const from = format(weekStart, ISO);
  const to = format(addDays(weekStart, 6), ISO);
  const days = useMemo(
    () =>
      eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }).map((d) =>
        format(d, ISO),
      ),
    [weekStart],
  );
  const householdId = params.get('household');
  const setWeek = (date: Date) =>
    setParams((p) => {
      p.set('week', format(date, ISO));
      return p;
    });

  const key = queryKeys.mealPlan(from, to, householdId ?? undefined);
  const week = useQuery({
    queryKey: key,
    queryFn: () => api.mealPlan.week({ from, to, householdId: householdId ?? undefined }),
  });
  const entries = week.data?.entries ?? [];

  const [dialog, setDialog] = useState<EntryDialogState>(null);
  const [remove, setRemove] = useState<MealPlanEntry | null>(null);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['meal-plan'] });

  const create = useMutation({
    mutationFn: (input: MealPlanEntryWriteInput) => api.mealPlan.create({ ...input, householdId }),
    onSuccess: () => {
      setDialog(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });
  const update = useMutation({
    mutationFn: ({
      entry,
      input,
    }: {
      entry: MealPlanEntry;
      input: Partial<MealPlanEntryWriteInput>;
    }) => api.mealPlan.update(entry.id, { ...input, version: entry.version }),
    onMutate: async ({ entry, input }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<MealPlanWeek>(key);
      queryClient.setQueryData<MealPlanWeek>(key, (prev) =>
        prev
          ? {
              ...prev,
              entries: prev.entries.map((e) =>
                e.id === entry.id
                  ? ({
                      ...e,
                      ...input,
                      customSlotName: input.customSlotName ?? e.customSlotName,
                    } as MealPlanEntry)
                  : e,
              ),
            }
          : prev,
      );
      return { previous };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      if (isVersionConflict(e)) toast.error(t('errors.VERSION_CONFLICT'));
      else toastError(e);
      invalidate();
    },
    onSuccess: () => {
      setDialog(null);
      invalidate();
    },
  });
  const del = useMutation({
    mutationFn: (entry: MealPlanEntry) => api.mealPlan.remove(entry.id),
    onSuccess: () => {
      setRemove(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });
  const toShopping = useMutation({
    mutationFn: api.mealPlan.toShoppingList,
    onSuccess: (list) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shopping.all });
      setShoppingOpen(false);
      toast.success(t('mealPlan.shopping.done'));
      navigate(`/shopping/${list.id}`);
    },
    onError: (e) => toastError(e),
  });

  const move = (entry: MealPlanEntry, target: SlotTarget) =>
    update.mutate({
      entry,
      input: { date: target.date, slot: target.slot, customSlotName: target.customSlotName },
    });

  const title = `${format(weekStart, 'P', { locale: dateLocale(i18n.language) })} – ${format(addDays(weekStart, 6), 'P', { locale: dateLocale(i18n.language) })}`;

  return (
    <PageContainer>
      <FeaturePageHeader
        title={t('mealPlan.title')}
        description={title}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShoppingOpen(true)}
              data-testid="meal-plan-shopping"
            >
              <ShoppingCart aria-hidden /> {t('mealPlan.shopping.button')}
            </Button>
            {aiEnabled && (
              <Button variant="outline" onClick={() => setSuggestOpen(true)}>
                <Sparkles aria-hidden /> {t('mealPlan.suggestions.button')}
              </Button>
            )}
          </>
        }
      >
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1" role="group" aria-label={t('mealPlan.weekNav')}>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('mealPlan.prevWeek')}
              onClick={() => setWeek(addDays(weekStart, -7))}
            >
              <ChevronLeft aria-hidden />
            </Button>
            <Button variant="outline" onClick={() => setWeek(new Date())}>
              {t('mealPlan.thisWeek')}
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('mealPlan.nextWeek')}
              onClick={() => setWeek(addDays(weekStart, 7))}
            >
              <ChevronRight aria-hidden />
            </Button>
          </div>
          <HouseholdSelect
            value={householdId}
            onChange={(v) =>
              setParams((p) => {
                if (v) p.set('household', v);
                else p.delete('household');
                return p;
              })
            }
            className="min-w-[12rem]"
          />
        </div>
      </FeaturePageHeader>

      {week.isLoading && <Spinner label={t('app.loading')} />}
      {week.error && <Alert variant="destructive">{describeError(week.error)}</Alert>}
      {week.data && (
        <WeekGrid
          days={days}
          entries={entries}
          onAdd={(target) => setDialog({ mode: 'create', target })}
          onEdit={(entry) => setDialog({ mode: 'edit', entry })}
          onMove={move}
          onRemove={setRemove}
          onMoveRequest={(entry) => setDialog({ mode: 'move', entry })}
        />
      )}

      <EntryDialog
        state={dialog}
        onClose={() => setDialog(null)}
        saving={create.isPending || update.isPending}
        onSubmit={(input) => {
          if (!dialog) return;
          if (dialog.mode === 'create') create.mutate(input);
          else if (dialog.mode === 'move')
            update.mutate({
              entry: dialog.entry,
              input: { date: input.date, slot: input.slot, customSlotName: input.customSlotName },
            });
          else update.mutate({ entry: dialog.entry, input });
        }}
      />
      <ConfirmDialog
        open={Boolean(remove)}
        onOpenChange={(o) => !o && setRemove(null)}
        title={t('mealPlan.deleteTitle')}
        destructive
        confirmLabel={t('common.delete')}
        loading={del.isPending}
        onConfirm={() => {
          if (remove) del.mutate(remove);
        }}
      />
      <ShoppingFromPlanDialog
        open={shoppingOpen}
        onOpenChange={setShoppingOpen}
        from={from}
        to={to}
        householdId={householdId}
        saving={toShopping.isPending}
        onSubmit={(body) => toShopping.mutate(body)}
      />
      <SuggestionsDialog
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        from={from}
        to={to}
        accepting={create.isPending}
        onAccept={async (suggestions) => {
          for (const s of suggestions) {
            await api.mealPlan.create({
              householdId,
              date: s.date,
              slot: (['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'].includes(s.slot)
                ? s.slot
                : 'CUSTOM') as MealPlanEntryWriteInput['slot'],
              customSlotName: ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'].includes(s.slot)
                ? null
                : s.slot,
              recipeId: s.recipeId,
              title: s.recipeId ? null : s.title,
              note: s.reason,
            });
          }
          invalidate();
        }}
      />
    </PageContainer>
  );
}
